import type { Diamond } from '../api/types'
import { displayStockId, formatCarat } from './format'

/**
 * Prefilled supplier-contact messages (Tej 2026-08-03): every WhatsApp /
 * email / SMS tap opens with the stone's identity + the buyer's interest
 * already written, so the enquiry lands specific instead of "hi".
 *
 * One text shape everywhere (desktop MediaModal + mobile StoneDetailScreen
 * + favorites pages) so suppliers see a consistent, recognizable format.
 * Uses displayStockId (the supplier's OWN reference when present) — that's
 * the id the supplier can actually look up.
 */

export function stoneEnquiryText(s: Diamond): string {
  const specs = [
    formatCarat(s.carat),
    s.shape,
    s.color,
    s.clarity,
  ]
    .filter(Boolean)
    .join(' ')
  const cert = s.lab && s.certNo ? ` (${s.lab} ${s.certNo})` : ''
  // Casual opener (Tej 2026-09-21) — the stock id lets the supplier look it up;
  // specs + the availability/price ask keep the enquiry specific and reply-worthy.
  return (
    `Hey! I was just browsing stones on DiaEx and I'm interested in your ` +
    `stock #${displayStockId(s)}${cert} — ${specs}. Is it still available, and what's your best price?`
  )
}

export function stoneEnquirySubject(s: Diamond): string {
  return `DiaEx enquiry — stock #${displayStockId(s)} (${formatCarat(s.carat)} ${s.shape ?? 'diamond'})`
}

/** General intro for contacting a favorited supplier (no specific stone). */
export function supplierIntroText(supplierName: string | null): string {
  return `Hi${supplierName ? ` ${supplierName}` : ''}! I'm a buyer on DiaEx — I'd like to talk about your inventory.`
}

export function waHref(phone: string, text: string): string {
  return `https://wa.me/${phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(text)}`
}

/**
 * The number the WhatsApp button should open for a supplier contact block.
 * Prefers the server-selected `whatsapp` (multi-country: the buyer-country line,
 * else the supplier's primary WhatsApp); falls back to the plain `phone` when a
 * stale API omits it, so the existing single-phone card keeps working. `Call`
 * and `Text` continue to use `phone` — WhatsApp is the only one that may differ.
 */
export function waTargetOf(
  supplier: { phone?: string | null; whatsapp?: string | null } | null | undefined,
): string | null {
  if (!supplier) return null
  return supplier.whatsapp || supplier.phone || null
}

/** `?&body` survives both iOS and Android SMS URL parsing. */
export function smsHref(phone: string, text: string): string {
  return `sms:${phone.replace(/[^0-9+]/g, '')}?&body=${encodeURIComponent(text)}`
}

export function mailtoHref(email: string, subject: string, body: string): string {
  return `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}
